import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SignOut from '../settings/sign_out';
import { MODAL } from '../settings/sign_out/sign_out';
import classes from './classes.module.scss';
import AppLink from '../routes/app_link';
import UserAvatarPicker from '../user_avatar_picker';
import ProfileInfoPopover from './profile_info_popover';
import HeaderSearch from './header_search';
import { getComponentStyle, componentStyles } from './component_styles';
import HeaderLogo from './header_logo';
import Const from '../../config/const';
import {
  matchBannerRoutes, getXfmFreeSetupMin, getXfmDefaultMinutes, isXFMActive, isPrerenderMode
} from '../../config/util';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';
import TopBanner from '../../../common/top_banner';
import { SvgIcon } from '../../../common/sprites';
import { appRoutes } from '../../config/app_routes';

const defaultAvatar = require('./img/home_placeholder.png');
const plusIcon = require('./img/popover_plus_icon.svg');
const filledPlusIcon = require('./img/popover_filled_plus_icon.svg');
const editIcon = require('./img/popover_edit_icon.svg');

const handleScroll = ({
  withTranparentTransition, transparent, setTransparent
}) => {
  // if (!withTranparentTransition || userXfmProgram) return;
  if (!withTranparentTransition) return;
  if (!transparent && window.scrollY === 0) setTransparent(true);
  if (transparent && window.scrollY > 0) setTransparent(false);
};

const getAvatar = ({ avatarUrlResize, avatarUrl }) => avatarUrlResize || avatarUrl || defaultAvatar;
const getAvatarIcon = ({ avatarUrlResize, avatarUrl }) => (avatarUrlResize || avatarUrl ? editIcon : plusIcon);
const getCreditButtonText = ({ clientAvailableCredit, t }) => {
  const availableCredit = parseFloat(clientAvailableCredit);
  if (availableCredit === 0) return t('header.addCredit');
  const formattedNumber = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(availableCredit);
  return `${ t('header.credits') } ${ formattedNumber }`;
};

function AppHeader({
  badgeCount, t, avatarUrl, avatarUrlResize, toggleDrawer, displayRegister,
  nickname, withTranparentTransition, itemProp, logOutSuccess, logOutClear, clientAvailableCredit,
  userBonusInfos, anonymousUserBonusInfos, userXfmProgram, userId
}) {
  const { routeKey } = useCurrentPath();
  const [transparent, setTransparent] = useState(
    window.scrollY === 0 && withTranparentTransition
  );

  const onScroll = () => {
    handleScroll({
      withTranparentTransition, transparent, setTransparent
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [transparent]);

  useEffect(() => {
    if (logOutSuccess) logOutClear();
  }, [logOutSuccess]);

  const getNicknameContainerClasses = () => {
    if (nickname) return classes.nicknameContainer;
    return `${ classes.nicknameContainer } ${ classes.empty }`;
  };

  function renderProfilePopoverHeader() {
    return (
      <div className={ classes.profilePopoverHeaderWrapper } suppressHydrationWarning>
        <div className={ classes.profilePopoverHeaderAvatarContainer }>
          <UserAvatarPicker>
            <div
              suppressHydrationWarning
              className={ classes.avatarContainer }
              style={ { '--size': '64px', '--radius':'24px' } }
            >
              <img
                suppressHydrationWarning
                alt=""
                src={ getAvatar({ avatarUrlResize, avatarUrl }) }
                style={ { '--radius': '20px' } }
                className={ classes.popoverAvatar }
              />
              <div className={ classes.avatarIconContainer }>
                <img className={ classes.avatarIcon } alt="" src={ getAvatarIcon({ avatarUrlResize, avatarUrl }) } />
              </div>
            </div>
          </UserAvatarPicker>
        </div>
        <div className={ classes.profilePopoverHeaderRightSide }>
          <div className={ getNicknameContainerClasses() } suppressHydrationWarning>
            { nickname || t('header.emptyNickname')}
          </div>
          <AppLink to={ appRoutes.addFunds } className={ classes.addCreditsButton }>
            <span>{ getCreditButtonText({ clientAvailableCredit, t }) }</span>
            <img src={ filledPlusIcon } alt={ t('header.addCredit') } />
          </AppLink>
        </div>
      </div>
    );
  }

  function renderProfilePopoverContent() {
    return (
      <div className={ classes.popoverContainer }>
        <div>
          <AppLink to={ appRoutes.settings } className={ classes.popoverItem }>
            <SvgIcon id="popover_settings" size={ 24 } />
            <div className={ classes.popoverItemText }>{ t('sideMenu.settings') }</div>
          </AppLink>
        </div>
        <SignOut theme={ MODAL } />
      </div>
    );
  }

  const showSignUp = () => {
    displayRegister({ shouldDisplay: true, signUpFlow: 'menu', screen: Const.authScreens.signUp });
  };

  const showSignIn = () => {
    displayRegister({ shouldDisplay: true, signUpFlow: 'menu', screen: Const.authScreens.signIn });
  };

  const renderJoinButton = () => (
    <button
      onClick={ showSignUp }
      type="button"
      className={ classes.loginButtonContainer }
      style={ getComponentStyle('transparent', componentStyles.joinButton) }
      suppressHydrationWarning
    >
      {t('join_now')}
    </button>
  );

  function renderProfileInfo() {
    if (window.currentUserId) {
      return (
        <div className={ classes.popoverContainer } suppressHydrationWarning>
          <ProfileInfoPopover
            title={ renderProfilePopoverHeader() }
            content={ renderProfilePopoverContent() }
          >
            <div
              className={ classes.profileInfoContainer }
              type="button"
              style={ getComponentStyle(transparent, componentStyles.profileInfoContainer) }
              suppressHydrationWarning
            >
              <div suppressHydrationWarning>
                { nickname || '' }
              </div>
              <div className={ classes.profileInfoContainerDivider } suppressHydrationWarning />
              <img className={ classes.avatar } src={ getAvatar({ avatarUrlResize, avatarUrl }) } alt="" />
            </div>
          </ProfileInfoPopover>
        </div>
      );
    }
    return (
      <div className={ classes.signInButtonsContainer }>
        <button onClick={ showSignIn } type="button" className={ classes.signIn } style={ getComponentStyle(transparent, componentStyles.signIn) } suppressHydrationWarning>
          { t('sign_in') }
        </button>
        {renderJoinButton()}
      </div>
    );
  }

  const topBannerProps = {
    userBonusInfos,
    anonymousUserBonusInfos,
    userXfmProgram,
    userId,
    shouldShowMarketingBanner: matchBannerRoutes(routeKey),
    xfmFreeSetupMin: getXfmFreeSetupMin(),
    xfmDefaultMinutes: getXfmDefaultMinutes(),
    isXFMActive: isXFMActive(),
    isPrerenderMode: isPrerenderMode()
  };

  return (
    <div id="mainHeaderID" data-testid="mainHeaderID">
      <TopBanner topBannerProps={ topBannerProps } />
      <div
        data-testid="header-container"
        suppressHydrationWarning
        className={ classes.headerContainer }
        style={ getComponentStyle(transparent, componentStyles.headerContainer) }
      >
        <div className={ classes.headerContent } suppressHydrationWarning>
          <div className={ classes.contentHolder } suppressHydrationWarning>
            <HeaderLogo
              toggleDrawer={ toggleDrawer }
              sideMenuIcon={ getComponentStyle(transparent, componentStyles.sideMenuIcon) }
              badgeCount={ badgeCount }
              transparent={ transparent }
              itemProp={ itemProp }
            />
            <div className={ classes.headerSearchAndProfileContainer }>
              <HeaderSearch />
              { renderProfileInfo() }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

AppHeader.propTypes = {
  children: PropTypes.node,
  t: PropTypes.func.isRequired,
  badgeCount: PropTypes.number,
  avatarUrl: PropTypes.string,
  avatarUrlResize: PropTypes.string,
  nickname: PropTypes.string,
  toggleDrawer: PropTypes.func.isRequired,
  displayRegister: PropTypes.func.isRequired,
  withTranparentTransition: PropTypes.bool,
  itemProp: PropTypes.object,
  logOutSuccess: PropTypes.bool.isRequired,
  logOutClear: PropTypes.func.isRequired,
  clientAvailableCredit: PropTypes.string,
  userBonusInfos: PropTypes.array,
  anonymousUserBonusInfos: PropTypes.array,
  userXfmProgram: PropTypes.object,
  userId: PropTypes.number
};

AppHeader.defaultProps = {
  children: null,
  badgeCount: 0,
  avatarUrl: null,
  avatarUrlResize: null,
  nickname: null,
  withTranparentTransition: false,
  itemProp: null,
  clientAvailableCredit: '0',
  userBonusInfos: null,
  anonymousUserBonusInfos: null,
  userXfmProgram: null,
  userId: null
};

export default AppHeader;
